import { Link, NavLink, useNavigate } from 'react-router-dom';
import { FlaskConical, Gift, X } from 'lucide-react';
import { format } from 'date-fns';

import { useBootstrap } from '@/lib/api/hooks';
import { useAuth } from '@/lib/auth/AuthProvider';
import { Icon as BrandIcon } from '@/lib/Icon';
import { cn } from '@/lib/cn';
import { supportChat } from './support/supportChatUi';
import { useLabsExperiment, useLabEvent } from "@/features/labs/labExperiment";
import { useClinicNavUnread } from '@/features/messaging/useMessagingUnread';

type NavIconKey =
  | 'home'
  | 'stack'
  | 'clinic'
  | 'orders'
  | 'learn'
  | 'upgrade'
  | 'account'
  | 'referral'
  | 'labs';

type NavItem = { to: string; label: string; icon: NavIconKey };

const NAV: NavItem[] = [
  { to: '/app', label: 'Home', icon: 'home' },
  { to: '/app/protocols', label: 'Stack', icon: 'stack' },
  { to: '/app/labs', label: 'Labs', icon: 'labs' as const },
  { to: '/app/clinic', label: 'Clinic', icon: 'clinic' },
  { to: '/app/orders', label: 'Orders', icon: 'orders' },
  { to: '/app/learn', label: 'Learn', icon: 'learn' },
  { to: '/app/shop', label: 'Shop', icon: 'upgrade' },
  { to: '/app/account', label: 'Account', icon: 'account' },
];

// Gated behind the `gift-referrals` experiment — appended only for the cohort.
const REFERRAL_ITEM: NavItem = {
  to: '/app/referrals',
  label: 'Referrals',
  icon: 'referral',
};

// Guided-tour spotlight anchors (features/tour/GuidedTour) — desktop steps.
const TOUR_TARGETS: Partial<Record<string, string>> = {
  '/app/protocols': 'stack-tab',
  '/app/clinic': 'clinic-tab',
  '/app/shop': 'shop-tab',
};

// Home uses the brand `Mark Line` logo; the rest pull from
// /public/icons/ which were dropped in by design.
function NavIcon({ kind, className }: { kind: NavIconKey; className?: string }) {
  if (kind === 'home') {
    return (
      <span className={cn('inline-flex items-center justify-center', className)}>
        <BrandIcon name="Mark Line" viewBox="0 0 32 30" />
      </span>
    );
  }
  if (kind === 'labs') return <FlaskConical className={className} size={16} strokeWidth={1.5} aria-hidden />;
  if (kind === 'referral') {
    return (
      <span className={cn('inline-flex items-center justify-center', className)}>
        <Gift size={16} strokeWidth={1.5} />
      </span>
    );
  }
  return (
    <img
      src={`/icons/icon-${kind}.svg`}
      alt=""
      aria-hidden
      className={cn('object-contain', className)}
    />
  );
}

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const { enabled: labsEnabled } = useLabsExperiment();
  const emitLab = useLabEvent();
  const { data: bootstrap } = useBootstrap();
  // Referrals + in-house AI support rolled out to 100% — the gift-referrals
  // and inhouse-chat experiments completed 2026-07-13 with variant-a winning.
  // Referrals sits just above Account (the last item).
  const navItems: NavItem[] = [...NAV.slice(0, -1).filter((item) => item.icon !== "labs" || labsEnabled), REFERRAL_ITEM, NAV[NAV.length - 1]];
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const now = new Date();

  async function handleSignOut() {
    if (!window.confirm('Sign out of System Labs?')) return;
    await signOut();
    navigate('/auth', { replace: true });
  }

  // Sum of unread per nav item. Right now Clinic is the only surface
  // with a badge; extend the map as more come online.
  const clinicUnread = useClinicNavUnread();
  const unreadByPath: Record<string, number> = {
    '/app/clinic': clinicUnread,
  };

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 bottom-0 w-[var(--sidebar-width)] bg-raised border-r border-line-subtle flex flex-col',
        'transition-transform duration-base ease-ease',
        'z-drawer md:z-sidebar',
        open ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
      )}
      aria-label="Main navigation"
    >
      <div className="px-15 h-60 flex items-center justify-between border-b border-line-subtle">
        <Link to="/app" onClick={onClose} aria-label="Home">
          <img
            src="/brand/system-wordmark.png"
            alt="System Labs"
            className="h-24 w-auto select-none"
            draggable={false}
          />
        </Link>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close menu"
          className="md:hidden w-32 h-32 flex items-center justify-center hover:bg-sunken"
        >
          <X size={16} strokeWidth={1.5} />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto">
        <ul>
          {navItems.map(({ to, label, icon }) => {
            const unread = unreadByPath[to] ?? 0;
            return (
              <li key={to} className="border-b border-line-subtle">
                <NavLink
                  to={to}
                  end={to === '/app'}
                  onClick={() => { if (to === "/app/labs") emitLab("blood_test_labs_clicked", { source: "sidebar" }); onClose(); }}
                  data-tour={TOUR_TARGETS[to]}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-15 px-15 h-40 type-body-md',
                      isActive
                        ? 'bg-[var(--surface-sunken)] text-ink'
                        : 'text-ink hover:bg-[var(--surface-sunken)]',
                    )
                  }
                >
                  <NavIcon
                    kind={icon}
                    className="w-18 h-18 text-ink-secondary shrink-0"
                  />
                  <span className="flex-1">{label}</span>
                  {unread > 0 && (
                    <span
                      className="inline-flex items-center justify-center min-w-18 h-18 px-5 rounded-pill bg-accent text-white type-data-xs"
                      aria-label={`${unread} unread`}
                    >
                      {unread}
                    </span>
                  )}
                  <span aria-hidden className="inline-flex items-center justify-center w-8 text-ink-muted shrink-0"><BrandIcon name="Arrow" viewBox="0 0 10 12" /></span>
                </NavLink>
              </li>
            );
          })}
        </ul>
        <div className="px-15 py-15">
          <div className="type-label-sm text-ink-muted">
            {format(now, 'EEEE, MMMM d').toUpperCase()}
          </div>
          <div className="type-label-xs text-ink-muted mt-5">
            {format(now, 'MM/dd/yyyy · h:mm a')}
          </div>
        </div>
      </nav>

      <div className="px-15 pt-15 pb-5">
        <button
          type="button"
          onClick={handleSignOut}
          className="type-label-sm text-ink-muted hover:text-ink underline-offset-4 hover:underline"
        >
          Sign out
        </button>
      </div>

      <div className="p-15 border-t border-line-subtle">
        <div className="type-label-sm mb-5">Need Help?</div>
        <p className="type-body-sm text-ink-muted">
          <button
            type="button"
            onClick={() => supportChat.open({ chooser: true })}
            className="text-ink underline underline-offset-4"
          >
            Chat with us
          </button>{' '}
          or contact our clinical team via email{' '}
          <a
            href={`mailto:${bootstrap?.help_contact.email ?? 'support@systemlabs.com'}`}
            className="text-ink underline underline-offset-4"
          >
            {bootstrap?.help_contact.email ?? 'support@systemlabs.com'}
          </a>{' '}
          or text us at{' '}
          <a
            href={`tel:${(bootstrap?.help_contact.phone ?? '+1 (949) 268-9869').replace(/[^\d+]/g, '')}`}
            className="text-ink underline underline-offset-4"
          >
            {bootstrap?.help_contact.phone ?? '(949) 268-9869'}
          </a>
          .
        </p>
      </div>
    </aside>
  );
}
